/**
 * Data type for Times.
 */
export class TimePart {

	/**
	 * The hour part of the time
	 */
	public Hours: number;

	/**
	 * The minute part of the time
	 */
	public Minutes: number;

	/**
	 * The second part of the time
	 */
	public Seconds: number;

	/**
	 * @constructor
	 * @param timeString The string to use to build the time.
	 */
	constructor(timeString: string) {
		const timeParts: string[] = timeString.split(':');
		const timeRegex: RegExp = /^(([0-1][0-9])|2[0-3])(:([0-5][0-9])){2}$/;

		// Ensure that the provided string matches the expected time mask
		if (!timeRegex.test(timeString)) {
			this.LogError('Invalid time format. Please use "HH:mm:ss".');
		}

		// Parse hours, minutes, and seconds from the string parts
		const hours: number = parseInt(timeParts[0]);
		const minutes: number = parseInt(timeParts[1]);
		const seconds: number = parseInt(timeParts[2]);

		// Validate hours, minutes, and seconds
		if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
			this.LogError('Invalid time format. Please use valid numbers for hours, minutes, and seconds.');
		}

		// Validate hours range (0 to 23)
		if (hours < 0 || hours > 23) {
			this.LogError('Invalid hour. Hours should be between 0 and 23.');
		}

		// Validate minute range (0 to 59)
		if (minutes < 0 || minutes > 59) {
			this.LogError('Invalid minute. Minutes should be between 0 and 59.');
		}

		// Validate second range (0 to 59)
		if (seconds < 0 || seconds > 59) {
			this.LogError('Invalid second. Seconds should be between 0 and 59.');
		}

		// If all validations pass, assign the values to the fields
		this.Hours = hours;
		this.Minutes = minutes;
		this.Seconds = seconds;
	}

	/**
	 * Overrides the toString method on TimePart for serialization.
	 * @returns The time formatted as 'HH:mm:ss'
	 */
	public toString(): string {
		const zeroPad = (num: number) => String(num).padStart(2, '0')
		return `${zeroPad(this.Hours)}:${zeroPad(this.Minutes)}:${zeroPad(this.Seconds)}`;
	}

	/**
	 * Converts the TimePart to a string with hours and minutes and no seconds.
	 * @returns The time formatted as 'HH:mm'
	 */
	public ToStringNoSeconds(): string {
		const zeroPad = (num: number) => String(num).padStart(2, '0')
		return `${zeroPad(this.Hours)}:${zeroPad(this.Minutes)}`;
	}

	/**
	 * Logs an error message.
	 * @param message The message to log.
	 */
	private LogError(message: string): void {
		console.error(message);
		throw new Error(message);
	}

}